import React from "react";

import Typography from "@mui/material/Typography";

import useSchedulingLinks from "@app/analytics/Calendar/hooks/useSchedulingLinks";
import DefaultLinks from "@app/analytics/Calendar/Scheduling/DefaultLinks";
import { TabContentContainer } from "@app/analytics/Calendar/styles";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import * as Events from "@app/analytics/tracking/events";
import { showToast } from "@app/common/hooks/useToasts";
import type {
  PostCustomSchedulingLinkBody,
  PatchSchedulingLinkBody,
  SchedulingLink,
} from "@app/common/types";
import { getDisplayUrl } from "@app/common/utils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import { Link } from "@rui/Link";
import Stack from "@rui/Stack";

import CustomLinks from "./CustomLinks";
import SchedulingCalendarBanner from "./SchedulingCalendarBanner";
import SchedulingEmptyState from "./SchedulingEmptyState";

const Scheduling = () => {
  usePageTitle("Calendar - Scheduling", "Calendar");

  const { rssLinkSettings, rootSchedulingLinkUrl } = useRssLinksSettings();
  const {
    isLoading,
    canCreateCustomLink,
    defaultLinks,
    customLinks,
    createLink,
    deleteLink,
    updateLink,
  } = useSchedulingLinks({ enabled: rssLinkSettings.isEnabled ?? false });

  if (!rssLinkSettings.isEnabled) {
    return <SchedulingEmptyState />;
  }

  const handleCreateLink = (
    link: PostCustomSchedulingLinkBody,
    { onSuccess }: { onSuccess: () => void }
  ) => {
    Events.createCustomLink();
    createLink.mutate(link, {
      onSuccess: () => {
        onSuccess();
        showToast({
          severity: "success",
          message: "Custom link created!",
        });
      },
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your link could not be created.",
        }),
    });
  };

  const handleUpdateLink = (
    updateBody: PatchSchedulingLinkBody,
    opts?: { onSuccess: () => void }
  ) => {
    updateLink.mutate(updateBody, {
      onSuccess: opts?.onSuccess,
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your link could not be updated.",
        }),
    });
  };

  const handleDeleteLink = (id: SchedulingLink["id"]) => {
    Events.deleteCustomLink();
    deleteLink.mutate(id, {
      onSuccess: () =>
        showToast({ severity: "success", message: "Custom link deleted!" }),
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your link could not be deleted.",
        }),
    });
  };

  return (
    <TabContentContainer>
      <SchedulingCalendarBanner />
      <Stack
        direction={{ xxs: "column", lg: "row" }}
        spacing={{ xxs: 6, lg: 8 }}
        sx={{ px: 2 }}
      >
        <Stack flexBasis="50%" spacing={1.5}>
          <Stack spacing={0.5}>
            <Typography variant="h3" fontWeight={500}>
              Default scheduling links
            </Typography>
            <Typography variant="labelReg" color="shades.neutral.500">
              Allows others to schedule a meeting with you. These will be
              provided as meeting duration options when someone visits your
              general scheduling link (
              <Link href={rootSchedulingLinkUrl}>
                {getDisplayUrl(rootSchedulingLinkUrl)}
              </Link>
              ) .
            </Typography>
          </Stack>
          <DefaultLinks
            isLoading={isLoading}
            links={defaultLinks}
            onToggleEnabled={handleUpdateLink}
          />
        </Stack>
        <Stack flexBasis="50%" spacing={1.5}>
          <Stack spacing={0.5}>
            <Typography variant="h3" fontWeight={500}>
              Custom scheduling links
            </Typography>
            <Typography variant="labelReg" color="shades.neutral.500">
              Send one of the links below to allow others to schedule a meeting
              based on specific constraints. These links do not expire.
            </Typography>
          </Stack>
          <CustomLinks
            canCreateLink={canCreateCustomLink}
            isCreating={createLink.isPending}
            isLoading={isLoading}
            isUpdating={updateLink.isPending}
            links={customLinks}
            onCreateLink={handleCreateLink}
            onDeleteLink={handleDeleteLink}
            onUpdateLink={handleUpdateLink}
          />
        </Stack>
      </Stack>
    </TabContentContainer>
  );
};

export default Scheduling;
